import React from 'react';
import { CAFE_IMAGE, CAFE_TEXT, CAFE_HIGHLIGHTS, WHATSAPP_LINK } from '../data/mockData';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Coffee, Sunrise, UtensilsCrossed, Flame, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';

const cafeIcons = [Sunrise, Coffee, UtensilsCrossed, Flame];

const CafePage = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Layout>
      <PageHeader
        title="Fuel for the"
        titleAccent="hills."
        subtitle="Good coffee, honest food, and enough chai to outlast any drizzle. Open to all — guests and wanderers alike."
        bgImage={CAFE_IMAGE}
      />

      <section className="pb-24 px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Highlights */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {CAFE_HIGHLIGHTS.map((item, i) => {
                  const Icon = cafeIcons[i];
                  return (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                      className="bg-[#1A1A1A] rounded-xl p-5 border border-[#222222] hover:border-[#D4A843]/20 transition-all duration-500"
                    >
                      <Icon size={20} className="text-[#D4A843] mb-3" />
                      <h4 className="text-[#F5F0E8] text-sm font-semibold mb-2">{item.title}</h4>
                      <p className="text-[#6A6050] text-xs leading-relaxed">{item.description}</p>
                    </motion.div>
                  );
                })}
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="bg-[#1A1A1A] rounded-xl p-6 border border-[#222222] mb-6"
              >
                <h4 className="text-[#D4A843] font-semibold text-sm mb-3">{CAFE_TEXT.bottomTitle}</h4>
                <p className="text-[#7A7060] text-sm leading-relaxed">{CAFE_TEXT.bottomText}</p>
              </motion.div>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#D4A843] text-[#1C1C1C] px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#E8B940] transition-colors duration-300 group/btn"
              >
                Reserve a Table <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
              </a>
            </div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden">
                <img src={CAFE_IMAGE} alt="MISFiTS Cafe" className="w-full h-[450px] md:h-[580px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-[#1C1C1C]/80 backdrop-blur-md rounded-xl p-4 border border-[#2A2A2A]">
                    <div className="flex items-center gap-3">
                      <Coffee size={18} className="text-[#D4A843]" />
                      <span className="text-[#D4A843] font-semibold font-heading">The MISFiTS Cafe</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[#D4A843]/20 rounded-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CafePage;
